import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Main SEO component - uses meta tags only in Helmet (no scripts)
// Scripts are added via useEffect to avoid react-helmet-async issues
export const SEO = ({ 
  title, 
  description, 
  image, 
  url, 
  type = 'website',
  keywords,
  article,
  product,
  recipe
}) => {
  const [settings, setSettings] = useState(null);
  const siteName = 'HomeLyph';
  const defaultTitle = 'HomeLyph - Premium Home Freeze Dryers';
  const defaultDescription = 'Preserve food for up to 25 years with HomeLyph freeze dryers. Perfect for families, homesteaders, and emergency preparedness.';
  const defaultImage = 'https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=1200';

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings`);
      setSettings(res.data);
    } catch (e) {
      // Settings not available
    }
  };

  const pageTitle = title ? `${title} | ${siteName}` : settings?.meta_title || defaultTitle;
  const pageDescription = description || settings?.meta_description || defaultDescription;
  const pageImage = image || defaultImage;
  const pageUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const pageKeywords = keywords || settings?.meta_keywords || 'freeze dryer, freeze drying, food preservation, HomeLyph';

  // Add structured data via useEffect to avoid Helmet script issues
  useEffect(() => {
    // Clean up previous schema scripts
    const existingScripts = document.querySelectorAll('script[data-schema="homelyph"]');
    existingScripts.forEach(script => script.remove());

    // Organization schema
    const orgSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "HomeLyph",
      "url": "https://homelyph.com",
      "logo": "https://homelyph.com/logo.png",
      "description": "Premium home freeze dryers for food preservation",
      "sameAs": [
        settings?.social_facebook,
        settings?.social_instagram,
        settings?.social_youtube,
        settings?.social_twitter
      ].filter(Boolean)
    };

    // Website schema
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "HomeLyph",
      "url": "https://homelyph.com"
    };

    // Build schemas array
    const schemas = [orgSchema, websiteSchema];

    // Product schema
    if (product) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.name,
        "description": product.description,
        "image": product.images || [product.image_url],
        "brand": { "@type": "Brand", "name": "HomeLyph" },
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": "USD",
          "availability": product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
        }
      });
    }

    // Article schema
    if (article) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.excerpt || article.description,
        "image": article.featured_image,
        "datePublished": article.created_at,
        "author": { "@type": "Person", "name": article.author?.name || "HomeLyph Team" },
        "publisher": { "@type": "Organization", "name": "HomeLyph" }
      });
    }

    // Recipe schema
    if (recipe) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Recipe",
        "name": recipe.title,
        "description": recipe.description,
        "image": recipe.featured_image,
        "author": { "@type": "Organization", "name": "HomeLyph" },
        "prepTime": recipe.prep_time ? `PT${recipe.prep_time}M` : undefined,
        "cookTime": recipe.cook_time ? `PT${recipe.cook_time}M` : undefined,
        "recipeYield": recipe.servings ? `${recipe.servings} servings` : undefined,
        "recipeCategory": recipe.categories?.[0],
        "keywords": recipe.tags?.join(', '),
        "recipeIngredient": recipe.ingredients?.map(i => `${i.amount}${i.unit ? ' ' + i.unit : ''} ${i.name}`),
        "recipeInstructions": recipe.instructions?.map((step, i) => ({
          "@type": "HowToStep",
          "position": i + 1,
          "text": step
        }))
      });
    }

    // FAQ schema (only if no product/article/recipe)
    if (!product && !article && !recipe) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is freeze drying?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Freeze drying is a food preservation method that removes moisture from food while preserving its nutritional value."
            }
          },
          {
            "@type": "Question",
            "name": "How long do freeze-dried foods last?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "When properly stored, freeze-dried foods can last 25 years or more."
            }
          }
        ]
      });
    }

    // Add schema scripts to head
    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'homelyph');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      // Cleanup on unmount
      const scripts = document.querySelectorAll('script[data-schema="homelyph"]');
      scripts.forEach(script => script.remove());
    };
  }, [settings, product, article, recipe]);

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={pageUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:site_name" content={siteName} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
    </Helmet>
  );
};

// Google Analytics component - injects GA script via useEffect to avoid Helmet issues
export const GoogleAnalytics = () => {
  const [gaId, setGaId] = useState(null);

  useEffect(() => {
    const fetchGaId = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        if (res.data?.google_analytics_id) {
          setGaId(res.data.google_analytics_id);
        }
      } catch (e) {
        // Settings not available
      }
    };
    fetchGaId();
  }, []);

  useEffect(() => {
    if (!gaId) return;

    // Remove existing GA scripts
    const existingGaScript = document.getElementById('ga-script');
    const existingGaInline = document.getElementById('ga-inline');
    if (existingGaScript) existingGaScript.remove();
    if (existingGaInline) existingGaInline.remove();

    // Add GA script tag
    const gaScript = document.createElement('script');
    gaScript.id = 'ga-script';
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(gaScript);

    // Add inline GA config
    const gaInline = document.createElement('script');
    gaInline.id = 'ga-inline';
    gaInline.textContent = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${gaId}');`;
    document.head.appendChild(gaInline);

    return () => {
      const script1 = document.getElementById('ga-script');
      const script2 = document.getElementById('ga-inline');
      if (script1) script1.remove();
      if (script2) script2.remove();
    };
  }, [gaId]);

  // Don't render anything - scripts are added via useEffect
  return null;
};

export default SEO;
